import { type CollectionSummaryJSONObject, type GeoPoint, IkeToken, type TagSummaryJSONObject } from '@ikegps/core-api';
import { action, computed, observable, makeObservable } from 'mobx';
import { ModalService } from '../../components/modal/modal.service';
import { NotificationService } from '../../components/notification.toast/notification.service';
import { DragonDomTypes } from '../../dom';
import { T, Translate } from '../../i18n';
import type { TagState } from '../../page/tag.state';
import { Services } from '../../services';
import { MobxState } from '../../state/mobx';
import { CurrentState } from '../../state/page.state';
import { VariantLayerState } from '../../state/variant.layer.state';
import { AppStore } from '../../store';
import { PointCache } from '../google/projection.cache';
import { BoundingString } from './bounding.string';
import { PointCollectionCache } from './point.collection.cache';

const BOUNDS_EXTEND_DELTA = 0.0005;
const BOUNDS_MIN_DISTANCE_METERS = 100;

const ZOOM_DETAIL_SWITCH_LEVEL = 19;
export const ZOOM_SWITCH_LEVEL = 15;
export const ZOOM_SWITCH_JOBS_LEVEL = 12;

export interface VisibleCollections {
	id: string;
	output: CollectionSummaryJSONObject[];
}

export interface TagMapUrlParams {
	location: string;
}

export class TagMapState extends MobxState {
	tagState: TagState;

	private map: google.maps.Map;

	bounds: google.maps.LatLngBounds;
	boundString = observable.box('', { name: 'BoundsString' });

	@observable zoom: number | null = null;

	constructor(tagState: TagState) {
		super();
		makeObservable(this);

		this.tagState = tagState;

		this.initMapLocation();
	}

	initMapLocation() {
		const locationString = Services.Route.params.get('location');
		const parsedBounds = BoundingString.fromString(locationString);

		if (parsedBounds == null) {
			return;
		}

		const actualBounds = new google.maps.LatLngBounds();
		actualBounds.extend(parsedBounds);

		this.bounds = actualBounds;
		this.zoom = parsedBounds.zoom;
	}

	@action setMap(map: google.maps.Map) {
		this.map = map;
	}

	@computed.struct get visibleTags(): string[] {
		this.boundString.get();
		const tags: string[] = [];

		for (const tag of this.tagState.filteredTags) {
			const tagSummary = AppStore.TagSummary.get(tag.objectId);
			if (tagSummary == null || tagSummary.center == null) {
				continue;
			}

			if (this.bounds == null || this.bounds.contains(tagSummary.center)) {
				tags.push(tag.objectId);
			}
		}

		return tags;
	}

	@computed.struct get visibleCollections(): string[] {
		this.boundString.get();
		// trace();
		if (this.bounds == null) {
			return [];
		}

		const visibleCollections = [];
		const selectedTagId = CurrentState.inspectedTagId;
		const inspectedCollectionRevision = CurrentState.inspectedCollectionRevision;

		let inspectedCollection: string = null;

		for (const tag of this.tagState.filteredTags) {
			const tagSummary = AppStore.TagSummary.get(tag.objectId);
			if (tagSummary == null) {
				continue;
			}
			const isTagSelected = selectedTagId === tag.objectId;

			// Job bounds are derived server-side from collection locations alone, so a job whose
			// collections are all locationless has none — even though it may have plenty of geography in
			// its drawn lines. Skipping it here would hide those lines entirely, so fall through and let
			// the per-collection checks below decide what is in view.
			const bounds = tagSummary.bounds;
			const isTagInBounds =
				bounds == null
					? true
					: this.bounds.intersects({
							north: bounds.ne.lat,
							east: bounds.ne.lng,
							south: bounds.sw.lat,
							west: bounds.sw.lng,
						});

			if (isTagInBounds === true) {
				// Three states, driven by the tokens:
				//  - layer UI active for this surface: show only the selected layer's collections (Existing
				//    shows the base poles; a design layer shows its variants).
				//  - feature on but no selector here (e.g. /map without the big-map token): the legacy view of
				//    base poles plus every variant dot (UFO-1054).
				//  - feature off: variants are hidden entirely — base poles only.
				// `get(revision)` lazily loads each summary, so a dot appears once its fetch resolves.
				let sourceRevisions: string[];
				if (this.tagState.layersActive(tag.objectId)) {
					sourceRevisions = VariantLayerState.visibleRevisions(tag.objectId) ?? [];
				} else if (VariantLayerState.featureEnabled()) {
					// Variants live under `variantsByParentUuid`, deliberately out of `revisions`.
					const variantRevisions = Object.values(tagSummary.variantsByParentUuid ?? {})
						.flat()
						.map((variant) => variant.revision);
					sourceRevisions = [...tagSummary.revisions, ...variantRevisions];
				} else {
					sourceRevisions = tagSummary.revisions;
				}
				for (const revision of sourceRevisions) {
					const summary = AppStore.CollectionSummary.get(revision);
					if (summary == null) {
						continue;
					}

					// A collection with no location still belongs on the map when it has drawn lines: the
					// line carries its own coordinates, so nothing has to stand in for the missing position.
					// Such a collection renders its line and no dot — a dot needs a real location.
					const location = summary.location;
					if (location == null && !this.hasLineGeometry(revision)) {
						continue;
					}

					// Render the inspected collection last
					if (inspectedCollectionRevision === summary.revision) {
						inspectedCollection = summary.revision;
					} else if (isTagSelected) {
						visibleCollections.push(summary.revision);
					} else if (
						location != null ? this.bounds.contains(this.getLatLng(location)) : this.hasLineGeometryInBounds(revision)
					) {
						visibleCollections.push(summary.revision);
					}
				}
			}
		}

		if (inspectedCollection != null) {
			visibleCollections.push(inspectedCollection);
		}

		return visibleCollections;
	}

	@action.bound centerMap() {
		const visibleBounds = new google.maps.LatLngBounds();

		const summaries: TagSummaryJSONObject[] = [];
		for (const tag of this.tagState.filteredTags) {
			const tagSummary = AppStore.TagSummary.get(tag.objectId);
			if (tagSummary != null) {
				summaries.push(tagSummary);
			}
		}

		if (summaries.length > 0) {
			for (const summary of summaries) {
				if (summary.bounds != null) {
					visibleBounds.extend(summary.bounds.ne).extend(summary.bounds.sw);
				}
			}
		} else {
			visibleBounds.extend({ lat: 80, lng: 180 });
			visibleBounds.extend({ lat: -80, lng: -180 });
		}

		this.map.fitBounds(visibleBounds);
		this.map.setCenter(visibleBounds.getCenter());
	}

	@action setBounds(bounds: google.maps.LatLngBounds) {
		const newBounds = bounds.toUrlValue();
		if (newBounds === this.boundString.get()) {
			return;
		}
		this.bounds = bounds;
		this.boundString.set(bounds.toUrlValue());
	}

	@action setZoom(zoom: number) {
		if (zoom === this.zoom) {
			return;
		}
		if (zoom !== this.zoom) {
			PointCache.reset();
		}
		this.zoom = zoom;
	}

	@action zoomToId(type: string, id: string) {
		if (type === DragonDomTypes.TAG) {
			return this.zoomToTag(id);
		}
	}

	@action zoomToTag(tagId: string): void {
		const tag = this.tagState.filteredTags.find((t) => t.objectId === tagId);
		if (tag == null) {
			return null;
		}

		const tagSummary = AppStore.TagSummary.get(tag.objectId);
		if (tagSummary == null || tagSummary.bounds == null) {
			return null;
		}

		Services.Analytics.trackEvent(Services.Analytics.EventCategory.BigMap, 'Tag', tag.objectId, 1);

		let bounds = new google.maps.LatLngBounds(tagSummary.bounds.sw, tagSummary.bounds.ne);
		bounds = this.ensureAppropriateBounds(bounds);

		this.map.fitBounds(bounds);
	}

	get showDetail(): boolean {
		return this.zoom >= ZOOM_DETAIL_SWITCH_LEVEL;
	}

	@action.bound async handleCollectionClick(event: MouseEvent, summary: CollectionSummaryJSONObject) {
		if (this.tryToggleSelection(event, summary)) {
			return;
		}

		const isHovered = CurrentState.hoverId === summary.revision;
		if (!this.showDetail && !isHovered) {
			return;
		}

		await this.inspectCollection(summary.revision);
	}

	private tryToggleSelection(event: MouseEvent, summary: CollectionSummaryJSONObject): boolean {
		const modifierHeld = event.metaKey || event.ctrlKey;
		const multiSelectEnabled = Services.Token.has(IkeToken.FEATURE_CTRL_SHIFT_SELECT);

		if (!modifierHeld || !multiSelectEnabled) {
			return false;
		}

		const tagId = summary.tags[0];
		if (tagId == null) {
			return false;
		}

		event.stopPropagation();
		if (event.preventDefault) {
			event.preventDefault();
		}

		CurrentState.Tag.toggleCollection(tagId, summary.uuid);
		return true;
	}

	@action private async inspectCollection(revision: string) {
		const collection = AppStore.Collection.get(revision);
		if (collection == null) {
			return;
		}

		// Mark the collection to link to. A variant is a read-only link target and is refused (warn, stay in
		// linking mode so a valid pole can be picked).
		if (CurrentState.CollectionLink.isLinking) {
			if (!CurrentState.CollectionLink.completeLinking(collection)) {
				NotificationService.warn({ key: T.SPAN_LINK_VARIANT_TARGET });
			}
			return;
		}

		// Inspect the collection
		const canChange = await this.checkChangeCollection(collection.revision);
		if (canChange) {
			CurrentState.setInspectedCollection(collection.revision);
		}
	}

	async zoomToCollection(revision: string) {
		if (revision === CurrentState.inspectedCollectionRevision) {
			this.handleZoomToCollection(revision);
			return;
		}

		const canChange = await this.checkChangeCollection(revision);
		if (!canChange) {
			return;
		}

		let newRevision = revision;
		const latestSummary = AppStore.CollectionSummary.getLatest(revision);
		if (latestSummary != null) {
			newRevision = latestSummary.revision;
		}

		CurrentState.setInspectedCollection(newRevision);
		this.handleZoomToCollection(newRevision);
	}

	@action private handleZoomToCollection(revision: string) {
		const collectionSummary = AppStore.CollectionSummary.get(revision);
		if (collectionSummary == null || collectionSummary.location == null) {
			return;
		}

		Services.Analytics.trackEvent(Services.Analytics.EventCategory.BigMap, 'Collection', collectionSummary.uuid, 1);

		let bounds = this.getLatLngBounds(collectionSummary.location);
		bounds = this.ensureAppropriateBounds(bounds);

		if (this.map != null) {
			this.map.fitBounds(bounds);
		}
	}

	checkChangeCollection(revision: string): Promise<boolean> {
		if (CurrentState.inspectedCollectionRevision == null) {
			return Promise.resolve(true);
		}

		if (CurrentState.inspectedCollectionRevision === revision) {
			return Promise.resolve(false);
		}

		return this.checkAndDiscardChanges();
	}

	async checkAndDiscardChanges(): Promise<boolean> {
		if (!CurrentState.CollectionEdit.isDirty && !CurrentState.CollectionLink.hasDirtyTargets) {
			CurrentState.CollectionEdit.reset();
			return true;
		}

		const response = await ModalService.open({
			title: T.DISCARD_CHANGES_TITLE,
			content: Translate(T.DISCARD_CHANGES_CONFIRM),
			okName: T.DISCARD_CHANGES_DISCARD,
			hasCancel: true,
		});

		const summary = AppStore.CollectionSummary.get(CurrentState.inspectedCollectionRevision);
		if (summary != null) {
			Services.Analytics.trackEvent(Services.Analytics.EventCategory.Mutate, 'Collection:discard', summary.uuid, 1);
		}

		if (response.ok) {
			CurrentState.CollectionEdit.reset();
		}
		return response.ok;
	}

	private ensureAppropriateBounds(bounds: google.maps.LatLngBounds): google.maps.LatLngBounds {
		const distance = google.maps.geometry.spherical.computeDistanceBetween(
			bounds.getNorthEast(),
			bounds.getSouthWest()
		);
		if (distance < BOUNDS_MIN_DISTANCE_METERS) {
			const extendPoint1 = new google.maps.LatLng(
				bounds.getNorthEast().lat() + BOUNDS_EXTEND_DELTA,
				bounds.getNorthEast().lng() + BOUNDS_EXTEND_DELTA
			);
			const extendPoint2 = new google.maps.LatLng(
				bounds.getNorthEast().lat() - BOUNDS_EXTEND_DELTA,
				bounds.getNorthEast().lng() - BOUNDS_EXTEND_DELTA
			);
			bounds.extend(extendPoint1);
			bounds.extend(extendPoint2);
		}

		return bounds;
	}

	/**
	 * Whether a collection has drawn lines to place on the map. Only loaded models carry line
	 * vertices, so an unloaded collection reads as having none — same as it does for every other
	 * line-rendering path on this map.
	 */
	private hasLineGeometry(revision: string): boolean {
		const lineFields = PointCollectionCache.getCollectionLineFields(revision);
		return lineFields != null && lineFields.length > 0;
	}

	/** True when any vertex of any drawn line falls inside the viewport. */
	private hasLineGeometryInBounds(revision: string): boolean {
		const lineFields = PointCollectionCache.getCollectionLineFields(revision);
		if (lineFields == null) {
			return false;
		}

		return lineFields.some((field) =>
			field.points.some((point) => this.bounds.contains({ lat: point.latitude, lng: point.longitude }))
		);
	}

	private getLatLng(loc: GeoPoint): google.maps.LatLng {
		return new google.maps.LatLng(loc.coordinates[1], loc.coordinates[0]);
	}

	private getLatLngBounds(loc: GeoPoint): google.maps.LatLngBounds {
		return new google.maps.LatLngBounds({
			lat: loc.coordinates[1],
			lng: loc.coordinates[0],
		});
	}
}
